var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState, } from 'react';
import { Typography } from '..';
import ChevronUp from '../../assets/icons/ChevronUp';
import CloseMark from '../../assets/icons/CloseMark';
import { useTheme } from '../../themes';
import { objectIsEmpty } from '../../utils/assertion';
import AccessoryWrapper from '../AccessoryWrapper';
import { Popper } from '../Popper';
import { Dropdown, MultiSelectItemWrapper, MultiSelectItem, } from './components';
import { buildSelectProps } from './modifiers';
import * as S from './styles';
export var Select = forwardRef(function (props, ref) {
    var theme = useTheme();
    var _a = buildSelectProps(props, theme), labelProps = _a.labelProps, wrapperProps = _a.wrapperProps, containerProps = _a.containerProps, label = _a.label, options = _a.options, dropdownProps = _a.dropdownProps, noOptionsText = _a.noOptionsText, devValue = _a.value, onSelect = _a.onSelect, onClear = _a.onClear, _b = _a.clearable, clearable = _b === void 0 ? true : _b, defaultValue = _a.defaultValue, searchable = _a.searchable, helperText = _a.helperText, helperTextTypographyProps = _a.helperTextTypographyProps, _c = _a.multiSelect, multiSelect = _c === void 0 ? false : _c, _d = _a.scrollYAtHeight, scrollYAtHeight = _d === void 0 ? 240 : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, rest = __rest(_a, ["labelProps", "wrapperProps", "containerProps", "label", "options", "dropdownProps", "noOptionsText", "value", "onSelect", "onClear", "clearable", "defaultValue", "searchable", "helperText", "helperTextTypographyProps", "multiSelect", "scrollYAtHeight", "disabled"]);
    var isControlled = useMemo(function () { return typeof devValue !== 'undefined'; }, [devValue]);
    var initValue = devValue || defaultValue;
    var initialValue = initValue
        ? Array.isArray(initValue)
            ? initValue
            : [initValue]
        : [];
    var _f = useState(null), containerRef = _f[0], setContainerRef = _f[1];
    var _g = useState(false), isOpen = _g[0], setIsOpen = _g[1];
    var _h = useState(initialValue[0]), internalValue = _h[0], setInternalValue = _h[1];
    var _j = useState(null), inputValue = _j[0], setInputValue = _j[1];
    var _k = useState(initialValue), multiSelectValues = _k[0], setMultiSelectValues = _k[1];
    var value = useMemo(function () { return (isControlled ? devValue : internalValue); }, [isControlled, devValue, internalValue]);
    var inputValueRef = createRef();
    function findMultiSelectIndex(option) {
        var values = __spreadArray([], multiSelectValues, true);
        var index = values.findIndex(function (i) { return i.value === option.value; });
        return { values: values, index: index };
    }
    var handleSetOnSelectValue = useCallback(function (currentValue) {
        // @ts-ignore
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(currentValue);
    }, []);
    var hasValue = (!!value && !objectIsEmpty(value)) || !!multiSelectValues.length;
    var multiOrSingleValue = multiSelect ? multiSelectValues : value;
    useEffect(function () {
        if (hasValue) {
            handleSetOnSelectValue(multiOrSingleValue);
        }
    }, []);
    var handleClickOption = useCallback(function (option) {
        var _a;
        var currOption = option;
        if (!multiSelect) {
            !isControlled && setInternalValue(option);
            setIsOpen(false);
        }
        if (multiSelect && !!option) {
            var _b = findMultiSelectIndex(option), values = _b.values, index = _b.index;
            if (index === -1) {
                values.push(option);
            }
            else {
                values.splice(index, 1);
            }
            setMultiSelectValues(values);
            currOption = values;
        }
        handleSetOnSelectValue(currOption);
        if (searchable) {
            (_a = inputValueRef === null || inputValueRef === void 0 ? void 0 : inputValueRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            setInputValue(null);
        }
    }, [isControlled, multiSelect, multiSelectValues, searchable, inputValueRef]);
    var handleClear = useCallback(function (e) {
        e === null || e === void 0 ? void 0 : e.preventDefault();
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        // NOTE: avoid infinite loop on ref.current.clear()
        if (e)
            onClear === null || onClear === void 0 ? void 0 : onClear();
        handleSetOnSelectValue(null);
        !isControlled && setInternalValue(null);
        setIsOpen(false);
        setMultiSelectValues([]);
        setInputValue(null);
    }, [isControlled, onClear]);
    var handleChangeInput = useCallback(function (e) {
        setInputValue(e.target.value);
    }, []);
    useImperativeHandle(ref, function () { return ({
        clear: handleClear,
        setValue: handleClickOption,
        value: multiOrSingleValue,
    }); }, [handleClear, handleClickOption, multiOrSingleValue]);
    var handleRemoveItem = useCallback(function (option) { return function (e) {
        var _a = findMultiSelectIndex(option), values = _a.values, index = _a.index;
        values.splice(index, 1);
        setMultiSelectValues(values);
        handleSetOnSelectValue(values);
        e === null || e === void 0 ? void 0 : e.stopPropagation();
    }; }, [multiSelectValues]);
    var maxWidth = props.maxWidth, width = props.width, minWidth = props.minWidth;
    var loseFocusOnPressTab = function (key) {
        if (key !== 'Tab')
            return;
        // É NECESSÁRIO PARA PERDER O FOCO QUANDO PRESSIONADO A TECLA "TAB"
        setTimeout(function () {
            setIsOpen(false);
        }, 200);
        // BUGA O SEARCHABLE
        // searchable && setInputValue(null);
    };
    var mTop = props.mTop, mBottom = props.mBottom, mLeft = props.mLeft, mRight = props.mRight;
    useEffect(function () {
        var _a;
        if (searchable && isOpen) {
            (_a = inputValueRef === null || inputValueRef === void 0 ? void 0 : inputValueRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [isOpen]);
    var showClearIcon = clearable && hasValue;
    var canClear = disabled ? undefined : handleClear;
    return (_jsxs(S.Container, __assign({}, containerProps, { mTop: mTop, mBottom: mBottom, mLeft: mLeft, mRight: mRight }, { style: { opacity: disabled ? 0.5 : 1 }, ref: setContainerRef, "data-testid": "select-container" }, { children: [label && (_jsx(Typography, __assign({ "data-testid": "select-label" }, labelProps, { children: label }))), _jsxs(S.Wrapper, __assign({ disabled: disabled }, wrapperProps, { maxWidth: maxWidth, width: width, minWidth: minWidth }, { isOpen: isOpen, onClick: function () { return (!disabled ? setIsOpen(true) : {}); } }, { children: [!multiSelect ? (_jsx(S.Input, __assign({ ref: inputValueRef }, rest, { "data-testid": searchable ? 'select-searchable' : 'select', onFocus: function () { return setIsOpen(true); }, onKeyDown: function (e) { return loseFocusOnPressTab(e.key); }, disabled: disabled, searchable: searchable, value: inputValue !== null && inputValue !== void 0 ? inputValue : ((value === null || value === void 0 ? void 0 : value.label) || ''), onChange: searchable ? handleChangeInput : undefined }))) : (_jsxs(MultiSelectItemWrapper, { children: [multiSelectValues.map(function (option) { return (_jsx(MultiSelectItem, { label: option.label, disabled: disabled, onRemove: handleRemoveItem(option) }, option.value)); }), searchable && (_jsx(S.Input, __assign({}, rest, { ref: inputValueRef, onFocus: function () { return setIsOpen(true); }, disabled: disabled, searchable: searchable, value: inputValue !== null && inputValue !== void 0 ? inputValue : ((value === null || value === void 0 ? void 0 : value.label) || ''), onChange: handleChangeInput }))), !searchable && !multiSelectValues.length && (_jsx("div", __assign({ "data-testid": "multi-select-empty", style: { flex: 1, display: 'flex', alignItems: 'center' } }, { children: _jsx(Typography, __assign({ type: "p2", color: theme.neutrals.gray7 }, { children: props.placeholder })) })))] })), _jsxs(S.RightAdornments, { children: [showClearIcon && (_jsx(AccessoryWrapper, __assign({ "data-testid": "select-clearable", mLeft: "nano", color: "inherit", onClick: canClear }, { children: _jsx(CloseMark, { fill: "inherit" }) }))), _jsx(S.Divider, {}), _jsx(AccessoryWrapper, __assign({ mRight: "quark", color: "inherit" }, { children: _jsx(S.ArrowRightContainer, __assign({ "data-testid": "select-open-close" }, { children: _jsx(ChevronUp, { fill: "inherit" }) })) }))] })] })), helperText && (_jsx(Typography, __assign({ "data-testid": "select-helper-text" }, helperTextTypographyProps, { children: helperText }))), _jsx(Popper, __assign({ referenceElement: containerRef, containerWidth: true, placement: "bottom-start", open: isOpen, onClose: function () { return setIsOpen(false); } }, { children: _jsx(Dropdown, __assign({}, dropdownProps, { options: options, onClickOption: handleClickOption, value: multiOrSingleValue, inputValue: inputValue, noOptionsText: noOptionsText, scrollYAtHeight: scrollYAtHeight })) }))] })));
});
